import { useState, useEffect, useRef, useCallback } from "react";
import { PendCanvas } from './pendulum';

// ─── Constants ────────────────────────────────────────────────────────────────
const MU    = 3.986e14;
const RE    = 6371e3;
const M_SAT = 12;
const ISP   = 70;
const G0    = 9.81;

// Highly elliptical orbit  (perigee 1 000 km, apogee 20 000 km)
// Keeps perigee far enough from Earth visually; apogee far so
// the satellite crawls → pulses are unmistakable
const R_PERI0 = RE + 1000e3;          // 7 371 km
const R_APO0  = RE + 20000e3;         // 26 371 km
const A0      = (R_PERI0 + R_APO0) / 2;
const E0      = (R_APO0 - R_PERI0) / (R_APO0 + R_PERI0);
const B0      = A0 * Math.sqrt(1 - E0 * E0);
// v at apogee  (counter-clockwise, at x=-R_APO0)
const V_APO0  = Math.sqrt(MU * (1 - E0) / (R_APO0 * (1 + E0)));

// ─── Pendulum constants (synced analogy: TO = apogee, FRO = perigee) ──────────
const L_PEND       = 2.34;                                          // arm length (m) — same as pendulum.jsx
const T_ORB_PEND   = 2 * Math.PI * Math.sqrt(Math.pow((R_PERI0 + R_APO0) / 2, 3) / MU);
const OMEGA_ORB_PEND = 2 * Math.PI / T_ORB_PEND;

function freshPendState() {
  const A0pend = 0.08;  // initial amplitude (rad) — pendulum starts at TO (apogee) side
  const h0 = L_PEND * (1 - Math.cos(A0pend));
  return { theta: A0pend, omega: 0, h: h0, maxH_to: h0, fired: false };
}

// Canvas geometry (computed once)
// Scale so full orbit fits with comfortable margins
// Semi-minor B0 limits vertical fit; R_APO0 limits horizontal
const CANVAS_W  = 1400;
const CANVAS_H  = 780;
const MARGIN    = 28;
const SC        = Math.min(
  (CANVAS_W - MARGIN * 2 - R_PERI0 - R_APO0 > 0
    ? (CANVAS_W - MARGIN * 2) / (R_PERI0 + R_APO0) : 1),
  (CANVAS_H / 2 - MARGIN) / B0
);                                      // px per metre
// Earth sits so apogee (–x direction) lands at left margin
const CX_EARTH  = MARGIN + R_APO0 * SC;
const CY_EARTH  = CANVAS_H / 2;
// Earth is drawn symbolically (fixed radius) so the orbit never overlaps it
const EARTH_VIS_R = 46;               // px — NOT physical scale

// ─── Orbit state factory ──────────────────────────────────────────────────────
function makeOrbit() {
  return {
    x: -R_APO0, y: 0, vx: 0, vy: -V_APO0,
    prevVr: 0.2,          // slightly positive → first step triggers apogee detection
    phase:   'wait',      // 'wait' | 'wait_pulse2'
    gapTimer: 0,
    burns:  0,
    propG:  0,
    apogee:  (R_APO0  - RE) / 1e3,
    perigee: (R_PERI0 - RE) / 1e3,
    vrHistory:  [],
    apoHistory: [{ n: 0, apo: (R_APO0 - RE) / 1e3 }],
    trail:      [],
    fired: false, fireDir: 0,
    simTime: 0,
    lastApo:      (R_APO0 - RE) / 1e3,
    deltaApoLast:  0,
    deltaApoTotal: 0,
    // Burn-window tracking: altitude profile between PULSE 1 and PULSE 2
    burnTrail:     [],   // [{relT (s), altKm}]
    burnStartAlt:  (R_APO0 - RE) / 1e3,   // altitude at PULSE 1 (km)
    burnStartTime: 0,    // simTime at PULSE 1
  };
}

// ─── RK4 integrator ───────────────────────────────────────────────────────────
function rk4(s, dt) {
  const d = (x, y, vx, vy) => {
    const r3 = Math.pow(x * x + y * y, 1.5);
    return { dx: vx, dy: vy, dvx: -MU * x / r3, dvy: -MU * y / r3 };
  };
  const { x, y, vx, vy } = s;
  const k1 = d(x, y, vx, vy);
  const k2 = d(x + k1.dx * dt / 2, y + k1.dy * dt / 2,
               vx + k1.dvx * dt / 2, vy + k1.dvy * dt / 2);
  const k3 = d(x + k2.dx * dt / 2, y + k2.dy * dt / 2,
               vx + k2.dvx * dt / 2, vy + k2.dvy * dt / 2);
  const k4 = d(x + k3.dx * dt, y + k3.dy * dt,
               vx + k3.dvx * dt, vy + k3.dvy * dt);
  return {
    ...s,
    x:  x  + (k1.dx  + 2*k2.dx  + 2*k3.dx  + k4.dx)  * dt / 6,
    y:  y  + (k1.dy  + 2*k2.dy  + 2*k3.dy  + k4.dy)  * dt / 6,
    vx: vx + (k1.dvx + 2*k2.dvx + 2*k3.dvx + k4.dvx) * dt / 6,
    vy: vy + (k1.dvy + 2*k2.dvy + 2*k3.dvy + k4.dvy) * dt / 6,
  };
}

// ─── Orbital elements ─────────────────────────────────────────────────────────
function elements(x, y, vx, vy) {
  const r   = Math.sqrt(x * x + y * y);
  const v2  = vx * vx + vy * vy;
  const eps = 0.5 * v2 - MU / r;
  const a   = -MU / (2 * eps);
  const h   = x * vy - y * vx;
  const ex  = (vy * h) / MU - x / r;
  const ey  = (-vx * h) / MU - y / r;
  const e   = Math.sqrt(ex * ex + ey * ey);
  return {
    a, e,
    b:        a * Math.sqrt(Math.max(0, 1 - e * e)),
    omega:    Math.atan2(ey, ex),
    apo_km:   (a * (1 + e) - RE) / 1e3,
    peri_km:  (a * (1 - e) - RE) / 1e3,
  };
}

// ─── Physics step + two-pulse firing ─────────────────────────────────────────
// PULSE 1: fires at apogee (vr: +→–). Gives outward radial Δv.
//          After kick, vr briefly goes positive again (satellite nudged outward).
// PULSE 2: fires at the NEXT vr zero-crossing (new mini-apogee) after gap_s
//          minimum has elapsed. This is event-driven, not timer-driven.
// Between pulses, burnTrail records altitude vs time so the close-up can
// render the parabolic rise and fall.
function stepOrbit(s, dt, J_ns, gap_s, thrusterOff = false, addToTrail = false) {
  let ns     = rk4(s, dt);
  ns.simTime = s.simTime + dt;

  // vr BEFORE any velocity kicks (pure orbital motion this sub-step)
  const r  = Math.sqrt(ns.x * ns.x + ns.y * ns.y);
  const vr = (ns.x * ns.vx + ns.y * ns.vy) / r;
  const rx = ns.x / r;
  const ry = ns.y / r;
  const dv = J_ns / M_SAT;

  let fired = false, fireDir = 0;
  let { phase, gapTimer, burns, propG, lastApo, deltaApoTotal } = s;
  let deltaApoLast = s.deltaApoLast;
  let burnTrail     = s.burnTrail;
  let burnStartAlt  = s.burnStartAlt;
  let burnStartTime = s.burnStartTime;

  const crossApogee = s.prevVr >= 0 && vr < 0;   // vr zero-crossing (+ → –)
  const altNow = (r - RE) / 1e3;                  // current altitude in km

  // ── PULSE 2: inward at next mini-apogee (event-based, after minimum gap) ─
  if (phase === 'wait_pulse2') {
    gapTimer = Math.max(0, gapTimer - dt);
    // Record altitude + vr profile during the raise window (sample every step)
    const vrNow = (ns.x * ns.vx + ns.y * ns.vy) / Math.sqrt(ns.x*ns.x + ns.y*ns.y);
    if (addToTrail) burnTrail = [...burnTrail.slice(-400), {
      relT: ns.simTime - burnStartTime,
      altKm: altNow, vr: vrNow,
      x: ns.x, y: ns.y,
    }];
    // Fire when: minimum gap has elapsed AND vr crosses zero (mini-apogee)
    if (!thrusterOff && gapTimer <= 0) {
      ns.vx -= dv * rx;
      ns.vy -= dv * ry;
      fired     = true;
      fireDir   = -1;
      phase     = 'wait';
      burns++;
      propG += M_SAT * (1 - Math.exp(-dv / (ISP * G0))) * 1000;
      const elNow    = elements(ns.x, ns.y, ns.vx, ns.vy);
      deltaApoLast   = elNow.apo_km - lastApo;
      deltaApoTotal += deltaApoLast;
      lastApo        = elNow.apo_km;
      // burnTrail is kept until next PULSE 1 so close-up can render it
    }
  }

  // ── PULSE 1: outward at apogee (first vr zero-crossing while waiting) ────
  // Guard: do NOT fire PULSE 1 in the same sub-step that PULSE 2 just fired —
  // that would overwrite fireDir=-1 with +1, making DAMPING invisible.
  if (!fired && !thrusterOff && phase === 'wait' && crossApogee) {
    ns.vx += dv * rx;
    ns.vy += dv * ry;
    fired    = true;
    fireDir  = 1;
    phase    = 'wait_pulse2';
    gapTimer = gap_s;
    burns++;
    propG += M_SAT * (1 - Math.exp(-dv / (ISP * G0))) * 1000;
    lastApo       = elements(s.x, s.y, s.vx, s.vy).apo_km;
    burnStartAlt  = altNow;
    burnStartTime = ns.simTime;
    // Start fresh burn trail; vr at T1 = dv (hull just got outward kick)
    burnTrail = [{ relT: 0, altKm: altNow, vr: dv, x: ns.x, y: ns.y }];
  }

  const nr  = Math.sqrt(ns.x * ns.x + ns.y * ns.y);
  const nvr = (ns.x * ns.vx + ns.y * ns.vy) / nr;
  const el  = elements(ns.x, ns.y, ns.vx, ns.vy);

  const newTrail = addToTrail
    ? [...s.trail.slice(-350), { x: ns.x, y: ns.y, vr: nvr }]
    : s.trail;

  let newVrHist = s.vrHistory;
  if (newVrHist.length === 0 ||
      ns.simTime - newVrHist[newVrHist.length - 1].t > 2) {
    newVrHist = [...newVrHist.slice(-250),
      { t: ns.simTime, vr: nvr, fired: fired && fireDir === 1 }];
  }

  const newApoHist = (fired && fireDir === -1)
    ? [...s.apoHistory.slice(-99), { n: Math.floor(burns / 2), apo: el.apo_km }]
    : s.apoHistory;

  return {
    ...ns,
    apogee: el.apo_km, perigee: el.peri_km,
    prevVr: nvr,
    phase, gapTimer, burns, propG,
    fired, fireDir,
    trail:        newTrail,
    vrHistory:    newVrHist,
    apoHistory:   newApoHist,
    lastApo, deltaApoLast, deltaApoTotal,
    burnTrail, burnStartAlt, burnStartTime,
  };
}

// ─── Canvas helpers ───────────────────────────────────────────────────────────
const toSX = (x) => CX_EARTH + x * SC;
const toSY = (y) => CY_EARTH - y * SC;

function arrowHead(ctx, x1, y1, x2, y2, color, sz = 9) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - sz * Math.cos(a - 0.38), y2 - sz * Math.sin(a - 0.38));
  ctx.lineTo(x2 - sz * Math.cos(a + 0.38), y2 - sz * Math.sin(a + 0.38));
  ctx.closePath();
  ctx.fill();
}

// ─── Main orbit canvas ────────────────────────────────────────────────────────
function drawOrbit(canvas, s, J_ns, gap_s) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  // ── Background + stars ──────────────────────────────────────────────────
  ctx.fillStyle = '#020810';
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 130; i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.15 + (i % 5) * 0.1})`;
    ctx.fillRect((i * 163 + 11) % W, (i * 107 + 43) % H, 1, 1);
  }

  // ── Initial orbit (reference, dashed) ──────────────────────────────────
  {
    const p0 = A0 * (1 - E0 * E0);
    ctx.beginPath();
    let first = true;
    for (let nu = 0; nu <= 2 * Math.PI; nu += 0.025) {
      const r  = p0 / (1 + E0 * Math.cos(nu));
      const sx = toSX(r * Math.cos(nu));   // omega0 = 0 → cos/sin directly
      const sy = toSY(r * Math.sin(nu));
      if (first) { ctx.moveTo(sx, sy); first = false; } else ctx.lineTo(sx, sy);
    }
    ctx.closePath();
    ctx.setLineDash([4, 6]);
    ctx.strokeStyle = '#0f2a20';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // ── Coloured trail (orange = vr>0 outward, cyan = vr<0 inward) ──────────
  for (let i = 1; i < s.trail.length; i++) {
    const p  = s.trail[i];
    const pp = s.trail[i - 1];
    const alpha = 0.12 + 0.45 * (i / s.trail.length);
    ctx.strokeStyle = p.vr > 0
      ? `rgba(255,152,0,${alpha})`
      : `rgba(0,210,180,${alpha})`;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(toSX(pp.x), toSY(pp.y));
    ctx.lineTo(toSX(p.x), toSY(p.y));
    ctx.stroke();
  }

  // ── Current orbit ellipse ───────────────────────────────────────────────
  {
    const el = elements(s.x, s.y, s.vx, s.vy);
    const p  = el.a * (1 - el.e * el.e);
    ctx.beginPath();
    let first = true;
    for (let nu = 0; nu <= 2 * Math.PI; nu += 0.02) {
      const r  = p / (1 + el.e * Math.cos(nu));
      const x  = r * Math.cos(nu + el.omega);
      const y  = r * Math.sin(nu + el.omega);
      const sx = toSX(x), sy = toSY(y);
      if (first) { ctx.moveTo(sx, sy); first = false; } else ctx.lineTo(sx, sy);
    }
    ctx.closePath();
    ctx.strokeStyle = '#0d9e6a';
    ctx.lineWidth   = 1.8;
    ctx.stroke();

    // Apogee marker
    const r_apo = el.a * (1 + el.e);
    const ax    = toSX(-r_apo * Math.cos(el.omega));
    const ay    = toSY(-r_apo * Math.sin(el.omega));
    ctx.beginPath();
    ctx.arc(ax, ay, 8, 0, Math.PI * 2);
    ctx.fillStyle   = s.fired && s.fireDir === 1 ? '#00ff88' : '#0d9e6a';
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur  = s.fired ? 18 : 0;
    ctx.fill();
    ctx.shadowBlur  = 0;
    ctx.fillStyle   = '#00ff88';
    ctx.font        = 'bold 13px monospace';
    ctx.fillText(`APO ${s.apogee.toFixed(2)} km`, ax - 40, ay - 14);

    // Perigee marker
    const r_peri = el.a * (1 - el.e);
    const px     = toSX(r_peri * Math.cos(el.omega));
    const py     = toSY(r_peri * Math.sin(el.omega));
    ctx.beginPath();
    ctx.arc(px, py, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#1a5a4a';
    ctx.fill();
    ctx.fillStyle = '#2a7a6a';
    ctx.font      = '12px monospace';
    ctx.fillText(`PERI ${s.perigee.toFixed(0)} km`, px + 8, py - 6);
  }

  // ── Earth (symbolic, not to physical scale) ─────────────────────────────
  // Drawn AFTER orbit so it sits on top and the orbit never looks buried in Earth
  const eg = ctx.createRadialGradient(
    CX_EARTH - 12, CY_EARTH - 12, 4,
    CX_EARTH, CY_EARTH, EARTH_VIS_R);
  eg.addColorStop(0, '#2a8ad4');
  eg.addColorStop(0.5, '#0a4a8a');
  eg.addColorStop(1, '#020810');
  ctx.beginPath();
  ctx.arc(CX_EARTH, CY_EARTH, EARTH_VIS_R, 0, Math.PI * 2);
  ctx.fillStyle = eg;
  ctx.fill();
  ctx.strokeStyle = '#0d3a6a';
  ctx.lineWidth   = 1.5;
  ctx.stroke();
  // Small glow ring so it's clearly distinct
  ctx.beginPath();
  ctx.arc(CX_EARTH, CY_EARTH, EARTH_VIS_R + 6, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(30,100,200,0.2)';
  ctx.lineWidth   = 4;
  ctx.stroke();
  ctx.fillStyle   = 'rgba(30,80,160,0.3)';
  ctx.font        = 'bold 13px monospace';
  ctx.textAlign   = 'center';
  ctx.fillText('EARTH', CX_EARTH, CY_EARTH + 4);
  ctx.textAlign   = 'left';

  // ── Satellite ───────────────────────────────────────────────────────────
  const sx  = toSX(s.x), sy  = toSY(s.y);
  const r   = Math.sqrt(s.x * s.x + s.y * s.y);
  const vr  = (s.x * s.vx + s.y * s.vy) / r;
  const rx  = s.x / r, ry = s.y / r;

  let satCol = vr > 30 ? '#ff9820' : vr < -30 ? '#00d4c0' : '#ffffff';
  if (s.fired) satCol = s.fireDir === 1 ? '#00ff88' : '#6ad4ff';

  ctx.beginPath();
  ctx.arc(sx, sy, 5, 0, Math.PI * 2);
  ctx.fillStyle   = satCol;
  ctx.shadowColor = satCol;
  ctx.shadowBlur  = s.fired ? 20 : 8;
  ctx.fill();
  ctx.shadowBlur = 0;

  // ── Thrust arrows ────────────────────────────────────────────────────────
  if (s.fired) {
    const dv    = (J_ns / M_SAT).toFixed(4);
    const AL    = 80;

    if (s.fireDir === 1) {
      // PULSE 1: outward (away from Earth)
      const ex = sx + rx * AL, ey = sy - ry * AL;
      ctx.strokeStyle = '#00ff88'; ctx.lineWidth = 3.5;
      ctx.shadowColor = '#00ff88'; ctx.shadowBlur = 12;
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.shadowBlur = 0;
      arrowHead(ctx, sx, sy, ex, ey, '#00ff88', 14);
      ctx.fillStyle = '#00ff88'; ctx.font = 'bold 13px monospace';
      ctx.fillText('PULSE 1', ex + 6, ey - 8);
      ctx.fillStyle = '#7affc0'; ctx.font = '11px monospace';
      ctx.fillText(`OUTWARD  Δv = +${dv} m/s`, ex + 6, ey + 8);
      ctx.fillText('(radially away from Earth)', ex + 6, ey + 22);
    } else {
      // PULSE 2: damping impulse toward Earth (same direction as satellite's fall)
      const ex = sx - rx * AL, ey = sy + ry * AL;
      ctx.strokeStyle = '#6ad4ff'; ctx.lineWidth = 3.5;
      ctx.shadowColor = '#6ad4ff'; ctx.shadowBlur = 12;
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.shadowBlur = 0;
      arrowHead(ctx, sx, sy, ex, ey, '#6ad4ff', 14);
      ctx.fillStyle = '#6ad4ff'; ctx.font = 'bold 13px monospace';
      ctx.fillText('PULSE 2', ex + 6, ey - 8);
      ctx.fillStyle = '#a0e8ff'; ctx.font = '11px monospace';
      ctx.fillText(`DAMPING  Δv = –${dv} m/s (toward Earth)`, ex + 6, ey + 8);
      ctx.fillText('(same dir as fall → +orbital energy)', ex + 6, ey + 22);
    }

    // Also show prograde direction (dashed yellow)
    const v   = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
    const pvx = s.vx / v, pvy = s.vy / v;
    ctx.setLineDash([3, 5]);
    ctx.strokeStyle = 'rgba(255,255,0,0.55)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx + pvx * 50, sy - pvy * 50);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,0,0.7)';
    ctx.font = '11px monospace';
    ctx.fillText('prograde', sx + pvx * 54, sy - pvy * 54);
  }

  // ── vr phase banner (top left) ───────────────────────────────────────────
  const phaseStr =
    s.phase === 'wait_pulse2' ? `GAP ${s.gapTimer.toFixed(2)}s → pulse 2 incoming` :
    vr >  80 ? '↗  APPROACHING APOGEE  (vr > 0, moving outward)' :
    vr < -80 ? '↘  FALLING TOWARD EARTH  (vr < 0)' :
               '≈ 0  AT APOGEE  — PULSE 1 FIRES HERE';
  const phaseCol =
    s.phase === 'wait_pulse2' ? '#ff8040' :
    vr > 80  ? '#ff9820' :
    vr < -80 ? '#00d4c0' : '#00ff88';
  ctx.fillStyle = phaseCol;
  ctx.font = 'bold 13px monospace';
  ctx.fillText(phaseStr, 10, 20);
  ctx.fillStyle = '#2a6a5a'; ctx.font = '11px monospace';
  ctx.fillText(`vr = ${vr.toFixed(2)} m/s`, 10, 36);

  // ── Legend (top right) ───────────────────────────────────────────────────
  ctx.textAlign = 'right';
  ctx.fillStyle = '#ff9820'; ctx.font = '11px monospace';
  ctx.fillText('■ outward (vr > 0)', W - 10, 18);
  ctx.fillStyle = '#00d4c0';
  ctx.fillText('■ inward (vr < 0)', W - 10, 32);
  ctx.fillStyle = '#0f2a20';
  ctx.fillText('- - initial orbit', W - 10, 46);
  ctx.textAlign = 'left';

  // ── vr Gauge (right side of canvas) ─────────────────────────────────────
  const gx = W - 20, gy = 55, gH = H - 80;
  const gW = 12;
  ctx.fillStyle = '#0a1510'; ctx.fillRect(gx, gy, gW, gH);
  ctx.strokeStyle = '#0f2a20'; ctx.lineWidth = 1; ctx.strokeRect(gx, gy, gW, gH);

  const midGy  = gy + gH / 2;
  const vrNorm = Math.min(1, Math.abs(vr) / 500);
  const barH   = vrNorm * (gH / 2);

  if (vr >= 0) {
    ctx.fillStyle = '#ff9820';
    ctx.fillRect(gx, midGy - barH, gW, barH);
  } else {
    ctx.fillStyle = '#00d4c0';
    ctx.fillRect(gx, midGy, gW, barH);
  }
  ctx.strokeStyle = '#1a5a4a'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(gx - 3, midGy); ctx.lineTo(gx + gW + 3, midGy); ctx.stroke();

  ctx.fillStyle = '#2a6a5a'; ctx.font = '7px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('vr', gx + gW / 2, gy - 3);
  ctx.fillStyle = '#ff9820'; ctx.fillText('+', gx + gW / 2, gy + 10);
  ctx.fillStyle = '#00d4c0'; ctx.fillText('–', gx + gW / 2, gy + gH - 3);
  ctx.textAlign = 'left';
}

// ─── vr chart canvas ──────────────────────────────────────────────────────────
function drawVrChart(canvas, s) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#020810'; ctx.fillRect(0, 0, W, H);

  const hist = s.vrHistory;
  if (hist.length < 2) {
    ctx.fillStyle = '#1a4a3a'; ctx.font = '9px monospace';
    ctx.fillText('Accumulating vr history...', 10, H / 2); return;
  }

  const pL = 46, pR = 10, pT = 18, pB = 20;
  const cW = W - pL - pR, cH = H - pT - pB;
  const tMin = hist[0].t, tMax = hist[hist.length - 1].t;
  const vrPk = Math.max(...hist.map(h => Math.abs(h.vr)), 100);
  const zY   = pT + cH / 2;

  // Zero line
  ctx.strokeStyle = '#0f2a20'; ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.moveTo(pL, zY); ctx.lineTo(pL + cW, zY); ctx.stroke();

  // Shaded fill
  ctx.beginPath();
  hist.forEach((pt, i) => {
    const x = pL + ((pt.t - tMin) / (tMax - tMin)) * cW;
    const y = zY - (pt.vr / vrPk) * (cH / 2);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  });
  ctx.lineTo(pL + cW, zY); ctx.lineTo(pL, zY); ctx.closePath();
  ctx.fillStyle = 'rgba(255,152,0,0.1)'; ctx.fill();

  // vr line — orange when positive, cyan when negative
  for (let i = 1; i < hist.length; i++) {
    const x1 = pL + ((hist[i-1].t-tMin)/(tMax-tMin))*cW;
    const y1 = zY - (hist[i-1].vr/vrPk)*(cH/2);
    const x2 = pL + ((hist[i].t-tMin)/(tMax-tMin))*cW;
    const y2 = zY - (hist[i].vr/vrPk)*(cH/2);
    ctx.strokeStyle = hist[i].vr >= 0 ? '#ff9820' : '#00d4c0';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }

  // Apogee-crossing ticks (where pulse 1 fires)
  ctx.setLineDash([2, 4]); ctx.strokeStyle = '#00ff8877'; ctx.lineWidth = 1;
  for (let i = 1; i < hist.length; i++) {
    if (hist[i - 1].vr >= 0 && hist[i].vr < 0) {
      const x = pL + ((hist[i].t - tMin) / (tMax - tMin)) * cW;
      ctx.beginPath(); ctx.moveTo(x, pT); ctx.lineTo(x, pT + cH); ctx.stroke();
    }
  }
  ctx.setLineDash([]);

  // Axis labels
  ctx.fillStyle = '#2a6a5a'; ctx.font = '7px monospace';
  ctx.fillText(`+${(vrPk/1000).toFixed(2)} km/s`, 2, pT + 4);
  ctx.fillText('0', pL - 12, zY + 3);
  ctx.fillText(`–${(vrPk/1000).toFixed(2)}`, 2, pT + cH - 2);
  ctx.fillStyle = '#ff9820'; ctx.fillText('↑ outward', pL + 4, pT + 12);
  ctx.fillStyle = '#00d4c0'; ctx.fillText('↓ inward',  pL + 4, pT + cH - 4);
  ctx.fillStyle = '#00ff8877'; ctx.fillText('┆ apogee (pulse 1)', pL + 70, pT + 12);
  ctx.fillStyle = '#1a5a4a'; ctx.font = '8px monospace';
  ctx.fillText('RADIAL VELOCITY vr (m/s)', pL, pT - 4);
}

// ─── Apogee altitude chart canvas ─────────────────────────────────────────────
function drawApoChart(canvas, s) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#020810'; ctx.fillRect(0, 0, W, H);

  const hist = s.apoHistory;
  if (hist.length < 2) {
    ctx.fillStyle = '#1a4a3a'; ctx.font = '9px monospace';
    ctx.fillText('Waiting for first complete pair...', 10, H / 2); return;
  }

  const pL = 54, pR = 10, pT = 18, pB = 20;
  const cW = W - pL - pR, cH = H - pT - pB;
  const vals = hist.map(h => h.apo);
  const lo   = Math.min(...vals);
  const hi   = Math.max(...vals);
  const rng  = Math.max(hi - lo, 0.001);

  // Grid
  for (let i = 0; i <= 4; i++) {
    const y  = pT + cH - (i / 4) * cH;
    ctx.strokeStyle = '#0a1a14'; ctx.lineWidth = 0.7;
    ctx.beginPath(); ctx.moveTo(pL, y); ctx.lineTo(pL + cW, y); ctx.stroke();
    ctx.fillStyle = '#2a5a4a'; ctx.font = '8px monospace';
    ctx.fillText((lo + (i / 4) * rng).toFixed(4), 2, y + 3);
  }

  // Fill area
  ctx.beginPath();
  hist.forEach((pt, i) => {
    const x = pL + (i / (hist.length - 1)) * cW;
    const y = pT + cH - ((pt.apo - lo) / rng) * cH;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  });
  ctx.lineTo(pL + cW, pT + cH); ctx.lineTo(pL, pT + cH); ctx.closePath();
  ctx.fillStyle = 'rgba(0,255,136,0.07)'; ctx.fill();

  // Line
  ctx.beginPath();
  hist.forEach((pt, i) => {
    const x = pL + (i / (hist.length - 1)) * cW;
    const y = pT + cH - ((pt.apo - lo) / rng) * cH;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  });
  ctx.strokeStyle = '#00ff88'; ctx.lineWidth = 2; ctx.stroke();

  // Dots
  hist.forEach((pt, i) => {
    const x = pL + (i / (hist.length - 1)) * cW;
    const y = pT + cH - ((pt.apo - lo) / rng) * cH;
    ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = '#00ff88'; ctx.fill();
  });

  // Label
  ctx.fillStyle = '#2a6a5a'; ctx.font = '8px monospace';
  ctx.fillText('APOGEE ALTITUDE (km)', pL, pT - 4);
  const rise = ((vals[vals.length - 1] - vals[0]) * 1000).toFixed(3);
  ctx.fillStyle = '#00ff88'; ctx.font = 'bold 9px monospace';
  ctx.fillText(`Δapo = +${rise} m total  (${hist.length - 1} pairs)`, pL + 4, pT + 13);
}

// ─── Smooth animation state (module-level, survives re-renders) ───────────────
// massPos: -1 = REPULSION wall (LEFT/away), +1 = DAMPER wall (RIGHT/Earth)
// Visual animation state — driven purely by wall-clock time since PULSE 1 fired.
// No state machine needed: one timestamp (repFiredMs) drives the whole cycle.
const _cycM = {
  pos: -1.0, repFiredMs: -999999, hullOff: 0,
  visScale: 1.0,          // animation speed scale (slows at high sim-speed)
  firePosX: -1, firePosY: 0, // satellite position when PULSE 1 fired
};
let   _cycLastNow = 0;
const REP_MS    = 500;              // ms REPULSION phase shown
const TRAVEL_MS = 1100;            // ms mass travels REP → DAMP
const DAMP_MS   = 600;             // ms DAMPING phase shown
const CYCLE_MS  = REP_MS + TRAVEL_MS + DAMP_MS;

// ─── Apogee EP-ADCS Cycle Canvas ──────────────────────────────────────────────
// Inverted orientation vs CyclePage:
//   LEFT  = REPULSION (away from Earth) → fires at apogee, pushes hull outward → further along orbit
//   RIGHT = DAMPER    (toward Earth)    → mass impacts here after crossing apogee
// Full orbit arc shown as background context with hull mechanism overlaid on sat position.
function drawApogeeZoom(canvas, s, J_ns, gap_s = 1) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  // ── Timing ──────────────────────────────────────────────────────────────────
  const now  = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const dtMs = Math.min(now - _cycLastNow, 60);
  _cycLastNow = now;

  const r_cur  = Math.sqrt(s.x * s.x + s.y * s.y);
  const vr_cur = (s.x * s.vx + s.y * s.vy) / r_cur;
  const dv_val = J_ns / M_SAT;
  const dv     = dv_val.toFixed(4);

  // ── Visual phase — scaled by visScale so animation stays near apogee at any speed
  // At 3000× speed the orbit wall-period is ~2.3s; without scaling, 2.2s animation
  // would show the satellite nearly completing a full orbit during damping.
  const VS      = _cycM.visScale;          // 0.12–1.0 depending on sim speed
  const REP_EFF  = REP_MS    * VS;
  const TRAV_EFF = TRAVEL_MS * VS;
  const DAMP_EFF = DAMP_MS   * VS;
  const CYCLE_EFF = REP_EFF + TRAV_EFF + DAMP_EFF;

  const t       = now - _cycM.repFiredMs;   // ms since last PULSE 1
  const firing1 = t >= 0         && t < REP_EFF;
  const inGap   = t >= REP_EFF   && t < REP_EFF + TRAV_EFF;
  const firing2 = t >= REP_EFF + TRAV_EFF && t < CYCLE_EFF;

  // mass travels from REPULSION(−1) to DAMPER(+1) during the TRAVEL window
  const travelProg = inGap
    ? (t - REP_EFF) / TRAV_EFF
    : (firing2 ? 1 : 0);

  // ── Mass position ────────────────────────────────────────────────────────────
  let massPos;
  if (firing2) {
    massPos = 1.0; _cycM.pos = 1.0;
  } else if (inGap) {
    massPos = -1.0 + 2.0 * travelProg; _cycM.pos = massPos;
  } else if (firing1) {
    massPos = -1.0; _cycM.pos = -1.0;
  } else {
    // stable hold: mass drifts back to repulsion wall
    _cycM.pos += (-1.0 - _cycM.pos) * Math.min(1, (dtMs / 1000) * 3.5);
    massPos = _cycM.pos;
  }

  const PHASE_COL  = firing1 ? '#00ff88'
                   : firing2 ? '#6ad4ff'
                   : inGap   ? '#ff9820'
                   :           '#0d9e6a';
  const PHASE_NAME = firing1 ? '① REPULSION  fires at apogee  →  hull displaced radially outward'
                   : firing2 ? '② DAMPING  mass hits damper  →  inward impulse adds orbital energy'
                   : inGap   ? `   COASTING  mass in transit  ·  gap = ${gap_s}s`
                   :           '   STABLE HOLD  ·  approaching apogee';

  // ── HEADER ──────────────────────────────────────────────────────────────────
  const HDR = 46;
  ctx.fillStyle = '#020d18'; ctx.fillRect(0, 0, W, HDR);
  ctx.strokeStyle = PHASE_COL + '70'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, HDR); ctx.lineTo(W, HDR); ctx.stroke();
  ctx.fillStyle = '#0d9e6a'; ctx.font = 'bold 12px "Segoe UI", Arial, sans-serif';
  ctx.fillText('EP-ADCS CYCLE  —  one cycle per apogee crossing', 10, 17);
  ctx.fillStyle = PHASE_COL; ctx.font = 'bold 14px "Segoe UI", Arial, sans-serif';
  ctx.fillText(PHASE_NAME, 10, 36);
  const vrCol = vr_cur > 80 ? '#ff9820' : vr_cur < -80 ? '#00d4c0' : '#00ff88';
  ctx.fillStyle = vrCol; ctx.font = '12px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'right';
  ctx.fillText(`vr = ${vr_cur.toFixed(2)} m/s  ·  apo = ${s.apogee.toFixed(2)} km`, W - 10, 17);
  if (s.deltaApoTotal > 0) {
    ctx.fillStyle = '#7affc0';
    ctx.fillText(`Δapo total = +${(s.deltaApoTotal * 1000).toFixed(3)} m`, W - 10, 36);
  }
  ctx.textAlign = 'left';

  // ══════════════════════════════════════════════════════════════════════════
  // BACKGROUND: Full orbit arc with Earth — the satellite travels this path
  // ══════════════════════════════════════════════════════════════════════════
  ctx.fillStyle = '#020810'; ctx.fillRect(0, HDR, W, H - HDR);
  ctx.fillStyle = PHASE_COL + '06'; ctx.fillRect(0, HDR, W, H - HDR);

  // Stars
  for (let i = 0; i < 70; i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.06 + (i % 5) * 0.04})`;
    ctx.fillRect((i * 173 + 19) % W, HDR + (i * 107 + 31) % (H - HDR - 50), 1, 1);
  }

  // Orbit arc in background (full orbit scaled to fill canvas height)
  const el_bg   = elements(s.x, s.y, s.vx, s.vy);
  const r_a_bg  = el_bg.a * (1 + el_bg.e);
  const r_p_bg  = el_bg.a * (1 - el_bg.e);
  const bg_mg   = 30;
  // APO_PAD shifts Earth rightward so the apogee satellite + hull aren't clipped
  const APO_PAD = 110;
  const bg_scX  = (W - bg_mg * 2 - APO_PAD) / (r_a_bg + r_p_bg);
  const bg_scY  = (H - HDR - bg_mg * 2) / (2 * el_bg.b);
  const bg_sc   = Math.min(bg_scX, bg_scY) * 0.92;
  const bg_EX   = bg_mg + APO_PAD + r_a_bg * bg_sc; // Earth x — shifted right
  const bg_EY   = HDR + (H - HDR) / 2;              // Earth y in bg
  const bX = (px) => bg_EX + px * bg_sc;
  const bY = (py) => bg_EY - py * bg_sc;

  // Earth globe (background)
  const eg = ctx.createRadialGradient(bg_EX - 8, bg_EY - 8, 3, bg_EX, bg_EY, 28);
  eg.addColorStop(0, '#2a8ad4cc'); eg.addColorStop(0.5, '#0a4a8acc'); eg.addColorStop(1, '#020810cc');
  ctx.beginPath(); ctx.arc(bg_EX, bg_EY, 28, 0, Math.PI * 2);
  ctx.fillStyle = eg; ctx.fill();
  ctx.strokeStyle = '#0d3a6acc'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = 'rgba(30,80,160,0.5)'; ctx.font = 'bold 11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
  ctx.fillText('EARTH', bg_EX, bg_EY + 4); ctx.textAlign = 'left';

  // ── Reference orbit (no thruster) — dim dotted line using initial elements ───
  // A0, E0 are module-level constants for the starting 1000×20000 km orbit.
  const p_ref = A0 * (1 - E0 * E0);
  ctx.beginPath();
  let refFirst = true;
  for (let nu = 0; nu <= 2 * Math.PI; nu += 0.03) {
    const rr = p_ref / (1 + E0 * Math.cos(nu));  // omega₀ = 0, apogee at –x
    const ix = bX(rr * Math.cos(nu)), iy = bY(rr * Math.sin(nu));
    if (refFirst) { ctx.moveTo(ix, iy); refFirst = false; } else ctx.lineTo(ix, iy);
  }
  ctx.closePath();
  ctx.strokeStyle = 'rgba(40,80,60,0.55)'; ctx.lineWidth = 1;
  ctx.setLineDash([2, 9]); ctx.stroke(); ctx.setLineDash([]);
  // Label near perigee (right side)
  ctx.fillStyle = 'rgba(40,100,70,0.75)'; ctx.font = '10px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('— no thruster', bX(R_PERI0), bY(0) - 12);
  ctx.textAlign = 'left';

  // ── Current orbit (with thrusters) — brighter solid line ────────────────────
  const p_bg = el_bg.a * (1 - el_bg.e * el_bg.e);
  ctx.beginPath();
  let bgFirst = true;
  for (let nu = 0; nu <= 2 * Math.PI; nu += 0.03) {
    const rr = p_bg / (1 + el_bg.e * Math.cos(nu));
    const ox = rr * Math.cos(nu + el_bg.omega);
    const oy = rr * Math.sin(nu + el_bg.omega);
    const ix = bX(ox), iy = bY(oy);
    if (bgFirst) { ctx.moveTo(ix, iy); bgFirst = false; } else ctx.lineTo(ix, iy);
  }
  ctx.closePath();
  ctx.strokeStyle = 'rgba(0,210,120,0.55)'; ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.fillStyle = 'rgba(0,210,120,0.80)'; ctx.font = '10px "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('— with thruster', bX(r_p_bg), bY(0) + 20);
  ctx.textAlign = 'left';

  // ── Orbit trail (actual trajectory, coloured by vr) ──────────────────────────
  // Orange = vr > 0 (climbing, post-REPULSION kick visible)
  // Cyan   = vr < 0 (falling, post-DAMPING kick visible)
  for (let i = 1; i < s.trail.length; i++) {
    const pp = s.trail[i - 1], cp = s.trail[i];
    const alpha = 0.10 + 0.40 * (i / s.trail.length);
    ctx.strokeStyle = cp.vr > 0 ? `rgba(255,152,0,${alpha})` : `rgba(0,210,180,${alpha})`;
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(bX(pp.x), bY(pp.y)); ctx.lineTo(bX(cp.x), bY(cp.y)); ctx.stroke();
  }

  // Apogee marker on background orbit
  const apo_bx = bX(-r_a_bg * Math.cos(el_bg.omega));
  const apo_by = bY(-r_a_bg * Math.sin(el_bg.omega));
  ctx.beginPath(); ctx.arc(apo_bx, apo_by, 6, 0, Math.PI * 2);
  ctx.fillStyle = firing1 ? '#00ff88' : 'rgba(0,255,136,0.3)';
  ctx.shadowColor = '#00ff88'; ctx.shadowBlur = firing1 ? 14 : 0;
  ctx.fill(); ctx.shadowBlur = 0;
  ctx.strokeStyle = '#00ff8866'; ctx.lineWidth = 1; ctx.stroke();
  ctx.fillStyle = '#00ff8899'; ctx.font = '10px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
  ctx.fillText('APOGEE', apo_bx, apo_by - 10);
  // ── Orbital energy added label at apogee ─────────────────────────────────────
  // ΔE = GMm·Δa / (2a²), where Δa = Δapo_km * 1000 / 2 (half goes to semi-major axis)
  if (s.deltaApoLast > 0) {
    const da_m  = s.deltaApoLast * 500;              // km → m, /2 for semi-major
    const dE_J  = (MU * M_SAT * da_m) / (2 * A0 * A0);
    const dE_mJ = dE_J * 1000;
    ctx.fillStyle = '#ffd060'; ctx.font = 'bold 10px "Segoe UI", Arial, sans-serif';
    ctx.fillText(`+ΔE = ${dE_mJ.toFixed(3)} mJ`, apo_bx, apo_by + 18);
    ctx.font = '9px "Segoe UI", Arial, sans-serif'; ctx.fillStyle = '#ffd06099';
    ctx.fillText('mass impact → orbital energy', apo_bx, apo_by + 30);
  }
  ctx.textAlign = 'left';

  // During active cycle, freeze satellite display at the apogee position
  // (prevents the dot drifting away from apogee during long wall-clock animations)
  const inCycle = firing1 || inGap || firing2;
  const dispX   = inCycle && _cycM.firePosX !== -1 ? _cycM.firePosX : s.x;
  const dispY   = inCycle && _cycM.firePosX !== -1 ? _cycM.firePosY : s.y;
  // Satellite dot on background orbit
  const sat_bx = bX(dispX), sat_by = bY(dispY);
  const satBC  = firing1 ? '#00ff88' : firing2 ? '#6ad4ff' : vr_cur > 0 ? '#ff9820' : '#00d4c0';
  ctx.beginPath(); ctx.arc(sat_bx, sat_by, 5, 0, Math.PI * 2);
  ctx.fillStyle = satBC; ctx.shadowColor = satBC; ctx.shadowBlur = 10; ctx.fill(); ctx.shadowBlur = 0;

  // ══════════════════════════════════════════════════════════════════════════
  // FOREGROUND: Compact hull AT satellite orbit position, oriented radially.
  //   local +x = OUTWARD from Earth = REPULSION end (fires at apogee)
  //   local -x = TOWARD  Earth      = DAMPER end   (mass impacts after apogee)
  //   The hull orbits with the satellite around Earth.
  // ══════════════════════════════════════════════════════════════════════════
  const massCol    = firing1 ? '#00ff88' : inGap ? '#ff9820' : firing2 ? '#6ad4ff' : '#0d9e6a';
  const coilActive = firing1;
  const dampActive = firing2;

  // Radial unit vector in screen coords (Earth to satellite = outward direction)
  const dx_sc  = sat_bx - bg_EX;
  const dy_sc  = sat_by - bg_EY;
  const len_sc = Math.sqrt(dx_sc * dx_sc + dy_sc * dy_sc);
  const rad_sx = len_sc > 1 ? dx_sc / len_sc : 1;
  const rad_sy = len_sc > 1 ? dy_sc / len_sc : 0;
  const ang_sc = Math.atan2(rad_sy, rad_sx);
  const perp_sx = -rad_sy, perp_sy = rad_sx;

  const HULL_TRACK = 40;   // half-track length in px
  const HULL_T     = 12;   // hull body thickness in px
  const MASS_R     = 5;    // mass ball radius
  const mass_lx    = -massPos * HULL_TRACK; // local x: massPos=-1->+40(REP), +1->-40(DAMP)

  // ── Smooth animated hull displacement (radial) ──────────────────────────────
  // REPULSION (firing1): hull swings OUTWARD — sin bell over REP_MS.
  // COASTING  (inGap):   hull returns toward neutral as mass travels.
  // DAMPING   (firing2): hull ramps sharply INWARD (reflects vr = vr + Δv_inward).
  //                      NO spring-back: hull stays inward until idle decays it.
  // Idle:                _cycM.hullOff decays smoothly to 0 from wherever it left off.
  let hullOffset;
  if (firing1) {
    const p = t / REP_EFF;
    hullOffset = 18 * Math.sin(Math.PI * p);       // outward bell
    _cycM.hullOff = hullOffset;
  } else if (inGap) {
    hullOffset = 18 * Math.exp(-4 * travelProg);   // decay back toward 0
    _cycM.hullOff = hullOffset;
  } else if (firing2) {
    const p2 = (t - REP_EFF - TRAV_EFF) / DAMP_EFF;
    // Fast inward ramp — hull gets pushed toward Earth and HOLDS (no spring-back)
    hullOffset = -14 * (1 - Math.exp(-7 * Math.min(1, p2)));
    _cycM.hullOff = hullOffset;
  } else {
    // Idle: decay from wherever the last phase left off (inward after DAMPING)
    _cycM.hullOff += (0 - _cycM.hullOff) * Math.min(1, (dtMs / 1000) * 2.5);
    hullOffset = _cycM.hullOff;
  }

  // ── Prograde (velocity) unit vector in screen coords ────────────────────────
  // At apogee prograde ⊥ radial. Shown as the direction impulses accumulate into.
  const vMag   = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
  const pro_sx = vMag > 0 ?  s.vx / vMag : -rad_sy;
  const pro_sy = vMag > 0 ? -s.vy / vMag :  rad_sx;  // canvas y flipped

  // ── Draw hull in rotated frame centred at displaced satellite position ───────
  const hullCX = sat_bx + hullOffset * rad_sx;
  const hullCY = sat_by + hullOffset * rad_sy;
  ctx.save();
  ctx.translate(hullCX, hullCY);
  ctx.rotate(ang_sc);

  // EMMS dashed centre track
  ctx.strokeStyle = '#1a3a2a'; ctx.lineWidth = 0.7; ctx.setLineDash([2, 3]);
  ctx.beginPath(); ctx.moveTo(-HULL_TRACK + 4, 0); ctx.lineTo(HULL_TRACK - 4, 0); ctx.stroke();
  ctx.setLineDash([]);

  // Hull body rectangle
  ctx.fillStyle = '#030e1c';
  ctx.strokeStyle = PHASE_COL + '99'; ctx.lineWidth = 1.5;
  ctx.fillRect(-HULL_TRACK, -HULL_T / 2, HULL_TRACK * 2, HULL_T);
  ctx.strokeRect(-HULL_TRACK, -HULL_T / 2, HULL_TRACK * 2, HULL_T);

  // REPULSION wall at local +HULL_TRACK (outward end)
  ctx.strokeStyle = coilActive ? '#00ff88' : '#00ff8840';
  ctx.lineWidth = coilActive ? 2.5 : 1;
  ctx.beginPath(); ctx.moveTo(HULL_TRACK, -HULL_T / 2 + 1); ctx.lineTo(HULL_TRACK, HULL_T / 2 - 1); ctx.stroke();
  for (let i = 0; i < 3; i++) {
    ctx.strokeStyle = coilActive ? '#00ff88bb' : '#00ff8828'; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(HULL_TRACK - 3 - i * 4, i % 2 === 0 ? -4 : 4);
    ctx.lineTo(HULL_TRACK - 3 - (i + 1) * 4, i % 2 === 0 ? 4 : -4);
    ctx.stroke();
  }
  if (coilActive) {
    ctx.beginPath(); ctx.arc(HULL_TRACK, 0, 20, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,255,136,0.14)'; ctx.fill();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * 2 * Math.PI;
      const l = 8 + (i % 2) * 8;
      ctx.strokeStyle = `rgba(0,255,136,${0.5 + (i % 2) * 0.15})`; ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(HULL_TRACK + Math.cos(a) * 5, Math.sin(a) * 5);
      ctx.lineTo(HULL_TRACK + Math.cos(a) * l, Math.sin(a) * l);
      ctx.stroke();
    }
  }

  // DAMPER wall at local -HULL_TRACK (toward-Earth end)
  ctx.strokeStyle = dampActive ? '#6ad4ff' : '#6ad4ff40';
  ctx.lineWidth = dampActive ? 2.5 : 1;
  ctx.beginPath(); ctx.moveTo(-HULL_TRACK, -HULL_T / 2 + 1); ctx.lineTo(-HULL_TRACK, HULL_T / 2 - 1); ctx.stroke();
  for (let i = 0; i < 3; i++) {
    const ky2 = -3 + i * 3;
    ctx.strokeStyle = dampActive ? '#6ad4ffbb' : '#6ad4ff28'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(-HULL_TRACK + 1, ky2); ctx.lineTo(-HULL_TRACK + 6, ky2 + 2); ctx.stroke();
  }
  if (dampActive) {
    ctx.beginPath(); ctx.arc(-HULL_TRACK, 0, 20, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(106,212,255,0.16)'; ctx.fill();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * 2 * Math.PI;
      const l = 8 + (i % 2) * 7;
      ctx.strokeStyle = `rgba(106,212,255,${0.5 + (i % 2) * 0.15})`; ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(-HULL_TRACK + Math.cos(a) * 5, Math.sin(a) * 5);
      ctx.lineTo(-HULL_TRACK + Math.cos(a) * l, Math.sin(a) * l);
      ctx.stroke();
    }
  }

  // Motion trail: mass travels from REP(+HULL_TRACK) to DAMP(-HULL_TRACK) during inGap.
  // Trail dots sit behind the mass in the +x direction (where it came from).
  if (inGap) {
    for (let ti = 1; ti <= 5; ti++) {
      const tx = Math.min(HULL_TRACK - 2, mass_lx + ti * 8);
      const alpha = ((6 - ti) / 6) * 0.7;
      ctx.beginPath(); ctx.arc(tx, 0, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,152,0,${alpha})`; ctx.fill();
    }
  }

  // Sliding mass ball
  ctx.beginPath(); ctx.arc(mass_lx, 0, MASS_R, 0, Math.PI * 2);
  ctx.fillStyle = massCol + '60'; ctx.fill();
  ctx.strokeStyle = massCol; ctx.lineWidth = 2; ctx.stroke();

  ctx.restore();

  // ── vr indicator: radial bar from hull centre ────────────────────────────────
  // Orange = outward (vr>0), Cyan = inward (vr<0). Length ∝ |vr|.
  {
    const VR_MAX_PX = 44;
    const vrFrac    = Math.min(1, Math.abs(vr_cur) / 600);
    const vrLen     = Math.max(3, vrFrac * VR_MAX_PX);
    const vrDir     = vr_cur >= 0 ? 1 : -1;
    const vrCol     = vr_cur >= 0 ? '#ff9820' : '#00d4c0';
    const vrEx = hullCX + vrDir * vrLen * rad_sx;
    const vrEy = hullCY + vrDir * vrLen * rad_sy;
    ctx.strokeStyle = vrCol; ctx.lineWidth = 2.5;
    ctx.shadowColor = vrCol; ctx.shadowBlur = 6;
    ctx.beginPath(); ctx.moveTo(hullCX, hullCY); ctx.lineTo(vrEx, vrEy); ctx.stroke();
    ctx.shadowBlur = 0;
    arrowHead(ctx, hullCX, hullCY, vrEx, vrEy, vrCol, 6);
    ctx.fillStyle = vrCol; ctx.font = 'bold 10px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(`vr ${vr_cur >= 0 ? '+' : ''}${vr_cur.toFixed(0)} m/s`, hullCX - perp_sx * 18, hullCY - perp_sy * 18);
    // Show Δvr from impulse during active phases
    if (firing1) {
      ctx.fillStyle = '#00ff88'; ctx.font = 'bold 9px "Segoe UI", Arial, sans-serif';
      ctx.fillText(`+Δvr = +${dv} m/s`, hullCX - perp_sx * 32, hullCY - perp_sy * 32);
    } else if (firing2) {
      ctx.fillStyle = '#6ad4ff'; ctx.font = 'bold 9px "Segoe UI", Arial, sans-serif';
      ctx.fillText(`Δvr = −${dv} m/s`, hullCX - perp_sx * 32, hullCY - perp_sy * 32);
    }
    ctx.textAlign = 'left';
  }

  // ── Labels near hull ends (anchored to displaced hull centre) ───────────────
  const repEndX = hullCX + rad_sx * (HULL_TRACK + 16) + perp_sx * 7;
  const repEndY = hullCY + rad_sy * (HULL_TRACK + 16) + perp_sy * 7;
  ctx.font = coilActive ? 'bold 11px "Segoe UI", Arial, sans-serif' : '10px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = coilActive ? '#00ff88' : '#00ff8860'; ctx.textAlign = 'center';
  ctx.fillText('REP', repEndX, repEndY);

  const dmpEndX = hullCX - rad_sx * (HULL_TRACK + 16) + perp_sx * 7;
  const dmpEndY = hullCY - rad_sy * (HULL_TRACK + 16) + perp_sy * 7;
  ctx.font = dampActive ? 'bold 11px "Segoe UI", Arial, sans-serif' : '10px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = dampActive ? '#6ad4ff' : '#6ad4ff60';
  ctx.fillText('DAMP', dmpEndX, dmpEndY);
  ctx.textAlign = 'left';

  // ── Impulse arrows: radial kick + prograde accumulation ─────────────────────
  // REPULSION (firing1): hull moves OUTWARD → radial arrow pointing away from Earth
  //   The radial kick, at apogee orientation, adds to the velocity vector prograde.
  // DAMPING (firing2):   hull moves INWARD → radial arrow pointing toward Earth
  //   The inward kick at a slightly rotated frame also adds prograde velocity.
  // Both show a smaller yellow prograde arrow to show the orbit-raising effect.
  if (firing1 || firing2) {
    const rCol  = firing1 ? '#00ff88' : '#6ad4ff';
    const rDir  = firing1 ? 1 : -1;  // +1 outward, -1 inward
    const fade  = firing1
      ? Math.sin(Math.PI * Math.min(1, t / REP_MS))
      : Math.sin(Math.PI * Math.min(1, (t - REP_MS - TRAVEL_MS) / DAMP_MS));

    // Radial impulse arrow (from displaced hull)
    const RL  = 52 * fade;
    const rSX = hullCX + rDir * rad_sx * (HULL_TRACK + 4);
    const rSY = hullCY + rDir * rad_sy * (HULL_TRACK + 4);
    const rEX = rSX + rDir * rad_sx * RL;
    const rEY = rSY + rDir * rad_sy * RL;
    ctx.strokeStyle = rCol; ctx.lineWidth = 3;
    ctx.shadowColor = rCol; ctx.shadowBlur = 14;
    ctx.beginPath(); ctx.moveTo(rSX, rSY); ctx.lineTo(rEX, rEY); ctx.stroke();
    ctx.shadowBlur = 0;
    arrowHead(ctx, rSX, rSY, rEX, rEY, rCol, 10);
    ctx.fillStyle = rCol; ctx.font = 'bold 11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(firing1 ? `+${dv} m/s OUTWARD` : `−${dv} m/s INWARD`,
      rEX + perp_sx * 20, rEY + perp_sy * 20);
    ctx.textAlign = 'left';

    // Prograde arrow — shows the component that adds to the velocity vector
    // (the radial kick is not purely prograde but the net orbital effect is)
    const PL  = 38 * fade;
    const pSX = hullCX + pro_sx * (HULL_TRACK + 4);
    const pSY = hullCY + pro_sy * (HULL_TRACK + 4);
    const pEX = pSX + pro_sx * PL;
    const pEY = pSY + pro_sy * PL;
    ctx.strokeStyle = `rgba(255,230,0,${0.75 * fade})`; ctx.lineWidth = 2;
    ctx.setLineDash([3, 4]);
    ctx.beginPath(); ctx.moveTo(pSX, pSY); ctx.lineTo(pEX, pEY); ctx.stroke();
    ctx.setLineDash([]);
    arrowHead(ctx, pSX, pSY, pEX, pEY, `rgba(255,220,0,${0.8 * fade})`, 7);
    ctx.fillStyle = `rgba(255,220,0,${0.8 * fade})`; ctx.font = '10px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('→ PROGRADE', pEX + perp_sx * 18, pEY + perp_sy * 18);
    ctx.textAlign = 'left';
  }

  // ── Status text at bottom-centre (fixed, always readable) ───────────────────
  const statusY = HDR + (H - HDR) * 0.90;
  if (inGap) {
    ctx.fillStyle = '#ff9820'; ctx.font = '11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('mass coasting  REPULSION → DAMPER', W / 2, statusY);
    const bW = 200, bXs = W / 2 - 100, bYs = statusY + 7;
    ctx.fillStyle = '#1a3a2a'; ctx.fillRect(bXs, bYs, bW, 5);
    ctx.fillStyle = '#ff9820'; ctx.fillRect(bXs, bYs, bW * travelProg, 5);
    ctx.strokeStyle = '#ff982055'; ctx.lineWidth = 0.7; ctx.strokeRect(bXs, bYs, bW, 5);
    ctx.textAlign = 'left';
  } else if (firing2) {
    ctx.fillStyle = '#6ad4ff'; ctx.font = '11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('mass hits DAMPER  →  inward impulse transfers energy to hull  →  orbit raised', W / 2, statusY);
    ctx.textAlign = 'left';
  } else if (firing1) {
    ctx.fillStyle = '#00ff88'; ctx.font = '11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('REPULSION fires at apogee  →  hull displaced radially outward  →  vr increases', W / 2, statusY);
    ctx.textAlign = 'left';
  } else {
    ctx.fillStyle = '#2a8a6a'; ctx.font = '11px "Segoe UI", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('stable hold  ·  approaching apogee  ·  mass at REPULSION wall', W / 2, statusY);
    ctx.textAlign = 'left';
  }

  // ── Phase timeline (bottom strip) ────────────────────────────────────────────
  const TL_Y = H - 44, TL_H = 30;
  const phaseTiles = [
    { name: 'STABLE HOLD',    color: '#0d9e6a', active: !firing1 && !firing2 && !inGap },
    { name: '① REPULSION',    color: '#00ff88', active: firing1 },
    { name: `COASTING ${gap_s}s`, color: '#ff9820', active: inGap },
    { name: '② DAMPING',      color: '#6ad4ff', active: firing2 },
  ];
  const tileW = W / phaseTiles.length;
  phaseTiles.forEach((ph, i) => {
    ctx.fillStyle = ph.active ? ph.color + '28' : '#04101c';
    ctx.fillRect(i * tileW, TL_Y, tileW - 2, TL_H);
    ctx.strokeStyle = ph.active ? ph.color : ph.color + '35';
    ctx.lineWidth = ph.active ? 2.5 : 0.8;
    ctx.strokeRect(i * tileW, TL_Y, tileW - 2, TL_H);
    ctx.fillStyle = ph.active ? ph.color : ph.color + '66';
    ctx.font = ph.active ? 'bold 12px "Segoe UI", Arial, sans-serif' : '11px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(ph.name, i * tileW + tileW / 2, TL_Y + TL_H / 2 + 4);
  });
  ctx.textAlign = 'left';
}

// ─── drawPendulum replaced by PendCanvas (pendulum.jsx) ──────────────────────
// Pendulum state is now React state (pendState) driven by orbit physics:
//   theta = A * cos(OMEGA_ORB_PEND * simTime)  → max TO (right) exactly at apogee
//   fired = orbit.fired && fireDir === 1        → pendulum fires when orbit fires at apogee
// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [running,      setRunning]      = useState(false);
  const [J,            setJ]            = useState(0.5);
  const [gapS,         setGapS]         = useState(1);
  const [speed,        setSpeed]        = useState(3000);
  const [thrusterOff,  setThrusterOff]  = useState(false);

  const [orbit,     setOrbit]     = useState(makeOrbit);
  const [pendState, setPendState] = useState(freshPendState);

  const orbitRef        = useRef(null);
  const rafRef          = useRef(null);
  const lastTsRef       = useRef(null);
  const pendARef        = useRef(0.08);        // pendulum amplitude (rad), grows each PULSE 1
  const pendLastT1Ref   = useRef(-9999);       // simTime of last PULSE 1 fire

  const canvOrbit = useRef(null);
  const canvVr    = useRef(null);
  const canvApo   = useRef(null);
  const canvZoom  = useRef(null);

  const reset = useCallback(() => {
    setRunning(false);
    const s = makeOrbit();
    orbitRef.current = s;
    setOrbit(s);
    lastTsRef.current = null;
    pendARef.current = 0.08;
    pendLastT1Ref.current = -9999;
    setPendState(freshPendState());
    setTimeout(() => {
      drawOrbit(canvOrbit.current, s, 0.5, 1);
      drawVrChart(canvVr.current, s);
      drawApoChart(canvApo.current, s);
      drawApogeeZoom(canvZoom.current, s, 0.5);
    }, 0);
  }, []);

  useEffect(() => { orbitRef.current = orbit; }, [orbit]);

  useEffect(() => {
    if (!running) { cancelAnimationFrame(rafRef.current); return; }
    const tick = (ts) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const wallDt = Math.min((ts - lastTsRef.current) / 1000, 0.05);
      lastTsRef.current = ts;

      const simDt = wallDt * speed;
      // Fewer sub-steps for performance — 60 is plenty for RK4 orbital accuracy
      const STEPS = 60;
      const dt    = simDt / STEPS;

      let s = orbitRef.current;
      for (let i = 0; i < STEPS; i++) {
        // Only copy trail every 5th sub-step to avoid 60× array copies per frame
        s = stepOrbit(s, dt, J, gapS, thrusterOff, i % 5 === 4);
        if (s.fired && s.fireDir === 1) {
          _cycM.repFiredMs = performance.now();
          // Freeze satellite visual at THIS position during the entire animation
          _cycM.firePosX = s.x;
          _cycM.firePosY = s.y;
          // Scale animation so it completes within ~8% of orbital wall-clock period
          // Prevents satellite from orbiting away from apogee during the animation
          const orbWallMs = (T_ORB_PEND / speed) * 1000;
          _cycM.visScale  = Math.min(1.0, Math.max(0.12, orbWallMs * 0.08 / CYCLE_MS));
        }
      }
      orbitRef.current = s;

      drawOrbit(canvOrbit.current, s, J, gapS);
      drawVrChart(canvVr.current, s);
      drawApoChart(canvApo.current, s);
      drawApogeeZoom(canvZoom.current, s, J, gapS);

      // ── Pendulum state: angle-based so theta = max-right (TO) exactly at
      //    apogee regardless of period drift or eccentricity changes ──────────
      const el_pend   = elements(s.x, s.y, s.vx, s.vy);
      const sat_angle = Math.atan2(s.y, s.x);
      const apo_angle = el_pend.omega + Math.PI;   // direction of apogee from Earth
      const relAngle  = sat_angle - apo_angle;      // 0 at apogee, π at perigee
      const pTheta  = pendARef.current * Math.cos(relAngle);
      const pFired  = s.fired && s.fireDir === 1;   // PULSE 1 = apogee = TO side
      if (pFired && s.simTime - pendLastT1Ref.current > T_ORB_PEND * 0.3) {
        pendARef.current     = Math.min(pendARef.current + 0.018, 0.55);
        pendLastT1Ref.current = s.simTime;
      }
      const pMaxH = L_PEND * (1 - Math.cos(pendARef.current));
      const pH    = L_PEND * (1 - Math.cos(Math.abs(pTheta)));
      setPendState({ theta: pTheta, omega: -pendARef.current * Math.sin(relAngle), h: pH, maxH_to: pMaxH, fired: pFired });

      setOrbit({ ...s });

      rafRef.current = requestAnimationFrame(tick);
    };
    lastTsRef.current = null;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running, J, gapS, speed, thrusterOff]);

  useEffect(() => {
    const s = makeOrbit();
    orbitRef.current = s;
    drawOrbit(canvOrbit.current, s, 0.5, 1);
    drawVrChart(canvVr.current, s);
    drawApoChart(canvApo.current, s);
    drawApogeeZoom(canvZoom.current, s, 0.5);
    // pendState initialised by useState(freshPendState) — no canvas call needed
  }, []);

  // ── Predicted raise per orbit pair ──────────────────────────────────────
  const omega_orb   = Math.sqrt(MU / Math.pow(A0, 3));
  const dv_pulse    = J / M_SAT;
  const dv_net_pro  = dv_pulse * omega_orb * gapS;
  const v_apo       = Math.sqrt(MU * (1 - E0) / (R_APO0 * (1 + E0)));
  const dapo_m      = 2 * R_APO0 * R_APO0 * v_apo * dv_net_pro / MU;
  const T_orb       = 2 * Math.PI * Math.sqrt(Math.pow(A0, 3) / MU);
  const wallPerOrb  = (T_orb / speed).toFixed(1);

  const bg   = '#020810';
  const card = '#060d18';
  const bord = '#0d2518';
  const acc  = '#0d9e6a';
  const hi   = '#00ff88';
  const mut  = '#1a4a3a';
  const txt  = '#90b0a8';

  const vr_cur = orbitRef.current
    ? (() => {
        const s = orbitRef.current;
        const r = Math.sqrt(s.x*s.x + s.y*s.y);
        return (s.x*s.vx + s.y*s.vy) / r;
      })()
    : 0;

  return (
    <div style={{ background: bg, minHeight: '100vh', color: txt,
      fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", padding: '14px 16px' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: mut, letterSpacing: '0.25em' }}>
          SPACEMANCRAFT · EP-ADCS
        </div>
        <div style={{ fontSize: 28, color: hi, fontWeight: 'bold', marginTop: 4 }}>
          Elliptical Orbit — Radial Apogee Manoeuvre
        </div>
        <div style={{ fontSize: 15, color: '#ff9820', marginTop: 5 }}>
          ↗ vr &gt; 0 → approaching apogee &nbsp;·&nbsp;
          vr = 0 → PULSE 1: outward (away from Earth) &nbsp;·&nbsp;
          after {gapS}s → PULSE 2: inward (retro-radial)
        </div>
        <div style={{ fontSize: 13, color: acc, marginTop: 3 }}>
          Orbit: 1 000 × 20 000 km · e = {E0.toFixed(3)} · T = {(T_orb/3600).toFixed(2)} h
          &nbsp;·&nbsp; 1 wall-clock orbit ≈ {wallPerOrb}s
        </div>
      </div>

      {/* Controls row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14,
        background: card, border: `1px solid ${bord}`, borderRadius: 6,
        padding: '12px 18px', marginBottom: 10 }}>

        <div>
          <div style={{ fontSize: 12, color: mut, marginBottom: 5 }}>
            IMPULSE J  per pulse (Ns)
          </div>
          <input type="range" min="0.1" max="3" step="0.05" value={J}
            onChange={e => setJ(+e.target.value)}
            style={{ width: '100%', accentColor: acc }}/>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: acc, fontWeight: 'bold' }}>{J.toFixed(2)} Ns</span>
            <span style={{ color: mut }}>Δv = {dv_pulse.toFixed(4)} m/s each</span>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: mut, marginBottom: 5 }}>
            PULSE GAP  (seconds between pulse 1 → 2)
          </div>
          <input type="range" min="0.5" max="120" step="0.5" value={gapS}
            onChange={e => setGapS(+e.target.value)}
            style={{ width: '100%', accentColor: '#ff9820' }}/>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#ff9820', fontWeight: 'bold' }}>{gapS}s</span>
            <span style={{ color: mut }}>net Δv_pro ≈ {(dv_net_pro * 1000).toFixed(4)} mm/s</span>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: mut, marginBottom: 5 }}>
            SIM SPEED  (× real time)
          </div>
          <input type="range" min="200" max="15000" step="200" value={speed}
            onChange={e => setSpeed(+e.target.value)}
            style={{ width: '100%', accentColor: '#6060ff' }}/>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#8080ff', fontWeight: 'bold' }}>{speed}×</span>
            <span style={{ color: mut }}>1 orbit ≈ {wallPerOrb}s wall</span>
          </div>
        </div>
      </div>

      {/* Run / Reset / Thruster Off / Status */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10,
        justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => setRunning(r => !r)} style={{
          background: running ? '#0a2a1a' : acc, color: running ? hi : '#000',
          border: `1px solid ${running ? hi : acc}`, borderRadius: 4,
          padding: '10px 36px', cursor: 'pointer', fontSize: 15,
          fontWeight: 'bold', letterSpacing: '0.1em'
        }}>{running ? '⏸ PAUSE' : '▶ RUN'}</button>

        <button onClick={reset} style={{
          background: 'transparent', color: mut, border: `1px solid ${bord}`,
          borderRadius: 4, padding: '10px 20px', cursor: 'pointer', fontSize: 14
        }}>↺ RESET</button>

        <button onClick={() => setThrusterOff(v => !v)} style={{
          background: thrusterOff ? '#2a0a00' : 'transparent',
          color: thrusterOff ? '#ff5010' : '#888',
          border: `2px solid ${thrusterOff ? '#ff5010' : bord}`,
          borderRadius: 4, padding: '10px 20px', cursor: 'pointer', fontSize: 14,
          fontWeight: thrusterOff ? 'bold' : 'normal',
          letterSpacing: '0.05em',
          boxShadow: thrusterOff ? '0 0 10px rgba(255,80,16,0.4)' : 'none'
        }}>
          {thrusterOff ? '🚫 THRUSTER OFF (showing free drift)' : '✓ THRUSTER ON'}
        </button>

        {/* Live phase indicator */}
        <div style={{ background: card, border: `1px solid ${
            Date.now() - _cycM.repFiredMs < REP_MS ? hi :
            Date.now() - _cycM.repFiredMs < CYCLE_MS ? '#6ad4ff' : bord}`,
          borderRadius: 4, padding: '6px 16px', fontSize: 13,
          transition: 'border-color 0.3s' }}>
          <span style={{ color: mut }}>Phase: </span>
          <span style={{ fontWeight: 'bold', color:
            orbit.phase === 'wait_pulse2' ? '#ff9820' :
            orbit.fired && orbit.fireDir === 1 ? hi :
            orbit.fired && orbit.fireDir === -1 ? '#6ad4ff' : '#2a7a6a' }}>
            {thrusterOff ? 'FREE DRIFT — no thrust'
              : orbit.phase === 'wait_pulse2'
              ? `COASTING — mass to damper${orbit.gapTimer > 0 ? ` · gap ${orbit.gapTimer.toFixed(1)}s` : ' · waiting for apogee'}`
              : orbit.fired && orbit.fireDir === 1 ? '◉ REPULSION — hull pushed outward'
              : orbit.fired && orbit.fireDir === -1 ? '◉ DAMPING — hull inward impulse'
              : '○ STABLE HOLD — approaching apogee'}
          </span>
        </div>
      </div>

      {/* Apogee close-up — TOP, full width (prominent) */}
      <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 6,
        overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ padding: '8px 16px', borderBottom: `1px solid ${bord}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: mut, letterSpacing: '0.1em' }}>
            EP-ADCS CYCLE — one cycle fires at each apogee &nbsp;·&nbsp;
            <span style={{ color: '#00ff88' }}>GREEN = REPULSION (hull away)</span> &nbsp;·&nbsp;
            <span style={{ color: '#6ad4ff' }}>CYAN = DAMPING (hull toward Earth)</span>
            {thrusterOff && <span style={{ color: '#ff5010', marginLeft: 12 }}>
              🚫 THRUSTER OFF — free drift
            </span>}
          </span>
          <span style={{ fontSize: 15, color: hi, fontWeight: 'bold' }}>
            APO {orbit.apogee.toFixed(3)} km
            {orbit.deltaApoTotal > 0 &&
              <span style={{ color: '#7affc0' }}>
                {' '}(+{(orbit.deltaApoTotal * 1000).toFixed(3)} m total rise)
              </span>}
          </span>
        </div>
        <canvas ref={canvZoom} width={900} height={620}
          style={{ display: 'block', width: '100%' }}/>
      </div>

      {/* vr chart + apo chart side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 6, overflow: 'hidden' }}>
          <canvas ref={canvVr} width={700} height={180}
            style={{ display: 'block', width: '100%' }}/>
        </div>
        <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 6, overflow: 'hidden' }}>
          <canvas ref={canvApo} width={700} height={180}
            style={{ display: 'block', width: '100%' }}/>
        </div>
      </div>

      {/* Live metrics grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 10 }}>
        {[
          { label: 'Radial velocity vr',
            value: `${vr_cur.toFixed(2)} m/s`,
            sub: vr_cur > 80 ? 'outward ↗' : vr_cur < -80 ? 'inward ↘' : '≈ 0 at apogee',
            color: vr_cur > 80 ? '#ff9820' : vr_cur < -80 ? '#00d4c0' : hi },
          { label: 'Apogee altitude',
            value: `${orbit.apogee.toFixed(3)} km`,
            sub: `started ${(R_APO0 - RE)/1e3} km`, color: hi },
          { label: 'Δapo last pair',
            value: orbit.deltaApoLast !== 0
              ? `+${(orbit.deltaApoLast * 1000).toFixed(4)} m`
              : '—',
            sub: `predicted +${(dapo_m * 100).toFixed(5)} cm`,
            color: '#7affc0' },
          { label: 'Total Δapo rise',
            value: orbit.deltaApoTotal > 0
              ? `+${(orbit.deltaApoTotal * 1000).toFixed(3)} m`
              : '—',
            sub: `${Math.floor(orbit.burns / 2)} orbit pairs · ${orbit.burns} burns`,
            color: '#7affc0' },
        ].map(m => (
          <div key={m.label} style={{ background: card, border: `1px solid ${bord}`,
            borderRadius: 6, padding: '12px 16px' }}>
            <div style={{ fontSize: 12, color: mut, marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 22, color: m.color, fontWeight: 'bold',
              fontFamily: 'monospace' }}>{m.value}</div>
            <div style={{ fontSize: 11, color: '#2a6a5a', marginTop: 4 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Physics explainer */}
      <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 6,
        padding: '14px 18px' }}>
        <div style={{ fontSize: 14, color: hi, letterSpacing: '0.12em', marginBottom: 12 }}>
          HOW THE RAISE WORKS — STEP BY STEP
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          {[
            { step:'①', title:'Approaching apogee', color:'#ff9820',
              body:`vr > 0 (moving outward).\nSatellite climbing away\nfrom Earth.\nTrail: ORANGE.\nNo thrust yet.` },
            { step:'②', title:'At apogee  — Pulse 1', color: hi,
              body:`vr = 0 exactly.\nPULSE 1: OUTWARD.\nΔv = J/M = ${dv_pulse.toFixed(4)} m/s\naway from Earth.\nSatellite nudges outward.` },
            { step:'③', title:`Gap: ${gapS}s`, color:'#ff8040',
              body:`T1 outward kick → vr>0.\nSatellite rises toward\nmini-apogee.\nΔh = dv²/(2g_apo)\n≈ ${((dv_pulse*dv_pulse/(2*MU/Math.pow(R_APO0,2)))).toFixed(3)} m above\nnominal apogee.\n(T2 waits for vr=0)` },
            { step:'④', title:'Pulse 2 — Retro-radial', color:'#00d4c0',
              body:`PULSE 2: INWARD\n(toward Earth).\nΔv = –J/M radial.\nFrame rotated → pulses\nNOT co-linear →\nnet prograde Δv_pro\n≈ ${(dv_net_pro*1000).toFixed(4)} mm/s.\n→ Δapo ≈ ${(dapo_m*100).toFixed(4)} cm/orbit.` },
          ].map(c => (
            <div key={c.step} style={{ background: '#040c14',
              border: `1px solid ${c.color}28`, borderRadius: 5, padding: '12px 14px' }}>
              <div style={{ fontSize: 15, color: c.color, fontWeight: 'bold',
                marginBottom: 6 }}>{c.step} {c.title}</div>
              <pre style={{ fontSize: 13, color: txt, margin: 0, lineHeight: 1.8,
                whiteSpace: 'pre-wrap', fontFamily: "'Courier New',monospace" }}>{c.body}</pre>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, background: '#040c14',
          border: `1px solid ${acc}44`, borderRadius: 5, padding: '12px 18px', fontSize: 13 }}>
          <span style={{ color: hi, fontWeight: 'bold' }}>NET FORMULA: </span>
          <span style={{ color: txt }}>
            Δv_prograde = (J/M) × ω_orb × gap
            = ({J}/{M_SAT}) × {omega_orb.toExponential(3)} × {gapS}
            = <span style={{ color: acc, fontWeight: 'bold' }}>{(dv_net_pro*1000).toFixed(5)} mm/s</span>
            &nbsp;→ Δapo ≈ <span style={{ color: hi, fontWeight: 'bold' }}>
              {(dapo_m*100).toFixed(5)} cm/orbit pair
            </span>
            {orbit.deltaApoTotal > 0 &&
              <span style={{ color: '#7affc0' }}>
                &nbsp;· measured total: +{(orbit.deltaApoTotal * 1000).toFixed(4)} m
              </span>}
          </span>
        </div>
      </div>

      {/* Full orbit + Pendulum */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10, marginTop: 10 }}>
        <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${bord}`,
            fontSize: 13, color: mut, letterSpacing: '0.1em', display: 'flex', justifyContent: 'space-between' }}>
            <span>ORBIT  (1 000 × 20 000 km)  ·  Earth drawn symbolic (not to scale)</span>
            {thrusterOff && <span style={{ color: '#ff5010' }}>🚫 THRUSTER OFF</span>}
          </div>
          <canvas ref={canvOrbit} width={CANVAS_W} height={CANVAS_H}
            style={{ display: 'block', width: '100%' }}/>
        </div>
        <div style={{ background: card, border: `1px solid ${pendState.fired ? hi : bord}`, borderRadius: 6, overflow: 'hidden', transition: 'border-color 0.1s' }}>
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${bord}`,
            fontSize: 13, color: mut, letterSpacing: '0.1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>PENDULUM ANALOGY — apogee (TO) ↔ perigee (FRO)</span>
            <span style={{ fontSize: 11, color: pendState.fired ? hi : mut }}>
              {pendState.fired ? '◉ TO-MAX FIRE' : `θ = ${(pendState.theta * 180 / Math.PI).toFixed(1)}°`}
            </span>
          </div>
          <PendCanvas s={pendState} W={700} H={480} style={{ width: '100%', height: 'auto' }} />
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: mut }}>
        SPACEMANCRAFT PVT LTD · EP-ADCS · 1 000 × 20 000 km orbit · RK4
      </div>
    </div>
  );
}
